import React from 'react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle } from 'lucide-react';

const ComplianceEditor = ({ project, onChange }) => {
  const requiresADA = project.requires_ada || false;
  const requiresTitle24 = project.requires_title24 || false;
  const requiresGreenBuilding = project.requires_green_building || false;
  const requiresLifeSafety = project.requires_life_safety || false;
  
  const adaNotes = project.ada_notes || '';
  const title24Notes = project.title24_notes || '';
  const greenBuildingNotes = project.green_building_notes || '';
  const lifeSafetyNotes = project.life_safety_notes || '';
  
  const isCommercial = [
    'tenant_improvement_food',
    'tenant_improvement_retail',
    'tenant_improvement_office',
    'commercial_new'
  ].includes(project.project_type);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Compliance & Requirements
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Manage ADA, Title 24, Green Building, and Life Safety compliance requirements
        </p>
      </div>
      
      {/* Compliance Requirements Overview */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Required Compliance Sections
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="requires_ada"
              checked={requiresADA}
              onCheckedChange={(checked) => onChange({ ...project, requires_ada: checked })}
            />
            <div className="flex-1">
              <Label htmlFor="requires_ada" className="cursor-pointer">
                ADA Accessibility
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Americans with Disabilities Act compliance
              </p>
            </div>
            {isCommercial && (
              <Badge variant="outline" className="text-orange-600 border-orange-600">
                Recommended
              </Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="requires_title24"
              checked={requiresTitle24}
              onCheckedChange={(checked) => onChange({ ...project, requires_title24: checked })}
            />
            <div className="flex-1">
              <Label htmlFor="requires_title24" className="cursor-pointer">
                Title 24 Energy Code
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                California Energy Code compliance
              </p>
            </div>
            <Badge variant="outline" className="text-green-600 border-green-600">
              Required
            </Badge>
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="requires_green_building"
              checked={requiresGreenBuilding}
              onCheckedChange={(checked) => onChange({ ...project, requires_green_building: checked })}
            />
            <div className="flex-1">
              <Label htmlFor="requires_green_building" className="cursor-pointer">
                Green Building (CALGreen)
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                California Green Building Standards
              </p>
            </div>
            <Badge variant="outline" className="text-green-600 border-green-600">
              Required
            </Badge>
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="requires_life_safety"
              checked={requiresLifeSafety}
              onCheckedChange={(checked) => onChange({ ...project, requires_life_safety: checked })}
            />
            <div className="flex-1">
              <Label htmlFor="requires_life_safety" className="cursor-pointer">
                Life Safety Plans
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Fire protection and egress requirements
              </p>
            </div>
            {isCommercial && (
              <Badge variant="outline" className="text-orange-600 border-orange-600">
                Recommended
              </Badge>
            )}
          </div>
        </div>
      </Card>
      
      {/* ADA Accessibility */}
      {requiresADA && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-2 mb-4">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              ADA Accessibility Compliance
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                Standard ADA Requirements Include:
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                <li>Accessible routes and entrances</li>
                <li>Door clearances and maneuvering spaces</li>
                <li>Restroom accessibility (if applicable)</li>
                <li>Drinking fountains and water features</li>
                <li>Signage and wayfinding</li>
                <li>Parking and loading zones</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="ada_notes">ADA Notes & Specific Requirements</Label>
              <Textarea
                id="ada_notes"
                value={adaNotes}
                onChange={(e) => onChange({ ...project, ada_notes: e.target.value })}
                placeholder="Enter specific ADA requirements for this project..."
                rows={6}
              />
            </div>
          </div>
        </Card>
      )}
      
      {/* Title 24 */}
      {requiresTitle24 && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-2 mb-4">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Title 24 Energy Code Compliance
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                Title 24 Requirements Include:
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                <li>Insulation and thermal performance</li>
                <li>Window glazing and solar heat gain</li>
                <li>HVAC system efficiency</li>
                <li>Water heating requirements</li>
                <li>Lighting power allowances</li>
                <li>Mandatory energy efficiency measures</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="title24_notes">Title 24 Notes & Calculations</Label>
              <Textarea
                id="title24_notes"
                value={title24Notes}
                onChange={(e) => onChange({ ...project, title24_notes: e.target.value })}
                placeholder="Enter Title 24 compliance notes and calculations..."
                rows={6}
              />
            </div>
          </div>
        </Card>
      )}
      
      {/* Green Building */}
      {requiresGreenBuilding && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-2 mb-4">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              CALGreen Building Standards
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                CALGreen Requirements Include:
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                <li>Water conservation measures</li>
                <li>Construction waste management</li>
                <li>Indoor environmental quality</li>
                <li>Low-VOC materials and finishes</li>
                <li>Storm water management</li>
                <li>Material resource efficiency</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="green_building_notes">Green Building Notes</Label>
              <Textarea
                id="green_building_notes"
                value={greenBuildingNotes}
                onChange={(e) => onChange({ ...project, green_building_notes: e.target.value })}
                placeholder="Enter CALGreen compliance notes and strategies..."
                rows={6}
              />
            </div>
          </div>
        </Card>
      )}
      
      {/* Life Safety */}
      {requiresLifeSafety && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-2 mb-4">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Life Safety Requirements
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                Life Safety Requirements Include:
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                <li>Occupancy load calculations</li>
                <li>Egress and exit capacity analysis</li>
                <li>Travel distance requirements</li>
                <li>Fire protection systems</li>
                <li>Emergency lighting and signage</li>
                <li>Fire alarm and detection systems</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="life_safety_notes">Life Safety Notes</Label>
              <Textarea
                id="life_safety_notes"
                value={lifeSafetyNotes}
                onChange={(e) => onChange({ ...project, life_safety_notes: e.target.value })}
                placeholder="Enter life safety calculations and requirements..."
                rows={6}
              />
            </div>
          </div>
        </Card>
      )}
      
      {/* No compliance selected warning */}
      {!requiresADA && !requiresTitle24 && !requiresGreenBuilding && !requiresLifeSafety && (
        <Card className="p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 bg-amber-50 dark:bg-amber-900/10">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-600 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            No Compliance Sections Selected
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Please select at least one compliance requirement above to document your project's regulatory requirements.
          </p>
        </Card>
      )}
    </div>
  );
};

export default ComplianceEditor;